// src/app/services/mbrs/sheet-applicability.service.ts
import { Injectable } from '@angular/core';
import { ConversionPhaseEnum } from '../../../types/commons';
import { SheetType, SheetOption } from '../../models/sheet-types';

/**
 * Inputs for {@link SheetApplicabilityService.computeEnabledSheets}. Every value
 * is a plain scalar the caller has already resolved from state — the service is
 * intentionally DI-free and pure so the mounted container and any headless
 * caller compute the SAME applicability from the SAME inputs (no drift), and so
 * it unit-tests without TestBed.
 *
 * Phase-aware state READS (which slot owns TypeOfSubmission / the method fields)
 * stay with the caller, because that is a state-access concern; this service
 * only owns the pure "given these facts, which sheets apply?" decision.
 */
export interface EnabledSheetsInput {
  /** Full candidate sheet list (the caller owns each option's label/icon). */
  sheets: readonly SheetOption[];
  /** Conversion phase; `null` = backend has not shipped the field / unresolved. */
  phase: ConversionPhaseEnum | null;
  /**
   * The active filing-info sheet (role0/role1) is complete.
   *
   * **Consulted in Phase 1 and unresolved (`null`) phase ONLY.** Phase 2
   * ignores it — see the VALIDITY clamp in `computeEnabledSheets` for why.
   * Callers should still supply it honestly; the waiver lives in one place.
   */
  isActiveFilingInfoValid: boolean;
  /** DATA-AVAILABILITY clamp (every phase): backend MBRS data exists. */
  hasMbrsData: boolean;
  /** DATA-AVAILABILITY clamp (every phase): MBRS data is mid-hydration. */
  isHydratingMbrsData: boolean;
  /** Resolved `ssmt-dei:TypeOfSubmission` (e.g. 'FS-MFRS', 'KFI-MPERS'). */
  typeOfSubmission: string;
  /** Resolved `ssmt-dei:DisclosureOfFinancialStatementsAuditStatus`. */
  auditStatus: string;
  methodFinancialPosition: string;
  methodProfitLoss: string;
  methodComprehensiveIncome: string;
  methodCashFlows: string;
  /** Roles hidden by the matched scenario (additional filtering layer). */
  scenarioHiddenRoles: readonly SheetType[];
}

/**
 * Single source of truth for sheet applicability — which sheets apply for a
 * given filing permutation / method / phase. Extracted verbatim from the
 * filing-form container's former `getEnabledSheets()` / `getRequiredSheets()`
 * so the inclusion logic lives in ONE place instead of being re-derived across
 * the container, the submit status builders, and the inbound-status applier
 * (the duplication that produced the inapplicable-`InvolveInSE` Review bug).
 *
 * NOTE: the KFI branch tests `methodFinancialPosition` against
 * `'Current/Non-current distinction'` while the FS branch tests
 * `'Current-Noncurrent'` — these are DIFFERENT strings in the source data and
 * are preserved verbatim from the original component logic (do not "unify" them
 * without a product ruling).
 *
 * Developer Note (2026-06-16): only `'FS-MFRS'` and `'FS-MPERS'` are ACTIVELY
 * supported today. The other Type-of-Submission branches (`'KFI-*'`, `'FS-EPC'`,
 * `'FS-BNM'`, `'FS-CLBG'`, …) are PLACEHOLDERS — carried over verbatim from the
 * original container logic so the seam is ready, but not exercised end-to-end.
 * They are highly susceptible to change when we officially need to support those
 * variations; treat their exact sheet sets / method-string matches as
 * provisional and re-verify against the spec sheets before relying on them.
 */
@Injectable({ providedIn: 'root' })
export class SheetApplicabilityService {
  /**
   * Filter the candidate sheet list down to the sheets that apply right now.
   * Pure port of the container's historical `getEnabledSheets()`.
   */
  computeEnabledSheets(input: EnabledSheetsInput): SheetOption[] {
    const {
      sheets,
      phase,
      isActiveFilingInfoValid,
      hasMbrsData,
      isHydratingMbrsData,
      typeOfSubmission,
      auditStatus,
      methodFinancialPosition,
      methodProfitLoss,
      methodComprehensiveIncome,
      methodCashFlows,
      scenarioHiddenRoles,
    } = input;

    // HARD BLOCK gate is phase-aware: Phase 2 clamps to role1 (filing-info),
    // otherwise to role0 (filing-prerequisites). Unknown phase defaults to the
    // role0 clamp to preserve pre-rollout behaviour.
    const activeFilingInfoSheet: SheetType =
      phase === ConversionPhaseEnum.Phase2
        ? 'filing-info'
        : 'filing-prerequisites';

    // DATA-AVAILABILITY clamp: nothing downstream can be rendered from state
    // that is absent or still arriving. Applies in every phase.
    if (!hasMbrsData || isHydratingMbrsData) {
      return sheets.filter((sheet) => sheet.value === activeFilingInfoSheet);
    }

    // VALIDITY clamp: the gate sheet must be COMPLETE before the rest unlock.
    // Deliberately NOT applied in Phase 2 — see the `isActiveFilingInfoValid`
    // field doc for the full reasoning. In short: a Phase-2 conversion has
    // already cleared Phase 1 and carries extracted data on every sheet, so
    // role1 is not a lock-you-out gate there; and because role1 republishes
    // `hasErrors` on every debounced keystroke, keeping the clamp meant
    // clearing one mandatory field collapsed the whole sheet selector to a
    // single entry mid-edit, which reads to a preparer as the conversion
    // reverting to Phase 1.
    //
    // Phase 1 is unaffected in practice (the PHASE GATE below already narrows
    // it to role0 regardless), so this clamp's only remaining live effect is
    // the unresolved-phase (`null`) case, where it preserves the pre-rollout
    // "complete role0 first" behaviour verbatim.
    if (phase !== ConversionPhaseEnum.Phase2 && !isActiveFilingInfoValid) {
      return sheets.filter((sheet) => sheet.value === activeFilingInfoSheet);
    }

    // PHASE GATE: Phase 1 exposes only Filing Prerequisites.
    if (phase === ConversionPhaseEnum.Phase1) {
      return sheets.filter((sheet) => sheet.value === 'filing-prerequisites');
    }

    // KFI submissions - only show essential sheets
    if (typeOfSubmission.startsWith('KFI-')) {
      return sheets.filter((sheet) => {
        // Hide Role-1 (filing-info) - Role-0 is the configuration layer
        if (sheet.value === 'filing-info') {
          return false;
        }

        // Always show filing prerequisites and scope
        if (
          sheet.value === 'filing-prerequisites' ||
          sheet.value === 'scope-of-filing'
        ) {
          return true;
        }

        // KFI-MFRS: Financial position + directors report + optional stock exchange
        if (typeOfSubmission === 'KFI-MFRS') {
          if (sheet.value === 'stock-exchange') {
            return true;
          }
          if (sheet.value === 'directors-report') {
            return true;
          }
          if (
            sheet.value === 'statement-of-financial-position' ||
            sheet.value === 'sub-classification'
          ) {
            return methodFinancialPosition === 'Current/Non-current distinction';
          }
          if (
            sheet.value === 'statement-of-financial-position-liquidity' ||
            sheet.value === 'sub-classification-liquidity'
          ) {
            return methodFinancialPosition === 'Order of liquidity';
          }
          return false;
        }

        // KFI-MPERS: Financial position + changes in equity + directors report
        if (typeOfSubmission === 'KFI-MPERS') {
          if (sheet.value === 'directors-report') {
            return true;
          }
          if (sheet.value === 'statement-of-changes-in-equity') {
            return true;
          }
          if (
            sheet.value === 'statement-of-financial-position' ||
            sheet.value === 'sub-classification'
          ) {
            return methodFinancialPosition === 'Current/Non-current distinction';
          }
          if (
            sheet.value === 'statement-of-financial-position-liquidity' ||
            sheet.value === 'sub-classification-liquidity'
          ) {
            return methodFinancialPosition === 'Order of liquidity';
          }
          return false;
        }

        // KFI-CLBG: Financial position + directors report
        if (typeOfSubmission === 'KFI-CLBG') {
          if (sheet.value === 'directors-report') {
            return true;
          }
          if (
            sheet.value === 'statement-of-financial-position' ||
            sheet.value === 'sub-classification'
          ) {
            return methodFinancialPosition === 'Current/Non-current distinction';
          }
          if (
            sheet.value === 'statement-of-financial-position-liquidity' ||
            sheet.value === 'sub-classification-liquidity'
          ) {
            return methodFinancialPosition === 'Order of liquidity';
          }
          return false;
        }

        return false;
      });
    }

    // FS-EPC: Exempted Private Company - minimal sheets
    if (typeOfSubmission === 'FS-EPC') {
      return sheets.filter(
        (sheet) =>
          sheet.value === 'filing-prerequisites' ||
          sheet.value === 'scope-of-filing' ||
          sheet.value === 'notes-corporate-information'
      );
    }

    // Full Statements (FS-*) - core sheets always visible; financial-statement
    // sheets gated by the method selections; then scenario/audit/phase layers.
    const coreSheetValues: SheetType[] = [
      'filing-prerequisites',
      'filing-info',
      'scope-of-filing',
      'directors-report',
      'statement-by-directors',
      'director-business-review',
      'auditors-report',
      'statement-of-changes-in-equity',
      'notes-corporate-information',
      'notes-accounting-policies',
      'notes-list-of-notes',
      'notes-issued-capital',
      'notes-related-party-transactions',
    ];

    if (typeOfSubmission === 'FS-MFRS' || typeOfSubmission === 'FS-BNM') {
      coreSheetValues.push('stock-exchange');
    }

    return sheets
      .filter((sheet) => {
        if (coreSheetValues.includes(sheet.value)) {
          return true;
        }

        switch (sheet.value) {
          case 'statement-of-financial-position':
          case 'sub-classification':
            return methodFinancialPosition === 'Current-Noncurrent';

          case 'statement-of-financial-position-liquidity':
          case 'sub-classification-liquidity':
            return methodFinancialPosition === 'Order of liquidity';

          case 'statement-of-profit-or-loss':
          case 'analysis-of-profit-or-loss':
            return methodProfitLoss === 'Function of expense';

          case 'statement-of-profit-or-loss-by-nature':
          case 'analysis-of-profit-or-loss-by-nature':
            return methodProfitLoss === 'Nature of expense';

          case 'statement-of-comprehensive-income':
            return methodComprehensiveIncome === 'After tax';

          case 'statement-of-comprehensive-income-before-tax':
            return methodComprehensiveIncome === 'Before tax';

          case 'statement-of-cash-flows-direct':
            return methodCashFlows === 'Direct';

          case 'statement-of-cash-flows-indirect':
            return methodCashFlows === 'Indirect';

          default:
            return false;
        }
      })
      .filter((sheet) => !scenarioHiddenRoles.includes(sheet.value))
      .filter((sheet) => {
        // Show auditors-report (Role-7) only if audit status is "Audited".
        if (sheet.value === 'auditors-report') {
          return auditStatus === 'Audited';
        }
        return true;
      })
      .filter((sheet) => {
        // Phase-aware visibility for the two filing-info gate sheets.
        const isPhase2 = phase === ConversionPhaseEnum.Phase2;
        if (sheet.value === 'filing-info') return isPhase2;
        if (sheet.value === 'filing-prerequisites') return !isPhase2;
        return true;
      });
  }

  /**
   * Required + optional sheets for a Type of Submission. Pure port of the
   * container's historical `getRequiredSheets()`.
   */
  computeRequiredSheets(typeOfSubmission: string): {
    required: SheetType[];
    optional: SheetType[];
  } {
    const required: SheetType[] = ['filing-prerequisites', 'scope-of-filing'];
    const optional: SheetType[] = [];

    // KFI submissions - minimal requirements
    if (typeOfSubmission.startsWith('KFI-')) {
      if (typeOfSubmission === 'KFI-MFRS') {
        required.push('statement-of-financial-position');
      } else if (typeOfSubmission === 'KFI-MPERS') {
        required.push(
          'statement-of-financial-position',
          'statement-of-changes-in-equity'
        );
      } else if (typeOfSubmission === 'KFI-CLBG') {
        required.push('statement-of-financial-position');
      }
      return { required, optional: [] };
    }

    // FS-EPC: Exempted Private Company - most sheets optional
    if (typeOfSubmission === 'FS-EPC') {
      required.push('notes-corporate-information');
      return { required, optional: [] };
    }

    // Full Statements (FS-*) - comprehensive requirements
    if (typeOfSubmission.startsWith('FS-')) {
      required.push(
        'directors-report',
        'statement-by-directors',
        'director-business-review',
        'auditors-report'
      );

      // Stock-exchange required for these submission types. Its VISIBILITY is
      // decided independently in computeEnabledSheets (KFI-MFRS via the KFI
      // branch; FS-MFRS / FS-BNM via the FS core list) and stays consistent.
      if (
        typeOfSubmission === 'FS-MFRS' ||
        typeOfSubmission === 'KFI-MFRS' ||
        typeOfSubmission === 'FS-BNM'
      ) {
        required.push('stock-exchange');
      }

      required.push('statement-of-changes-in-equity');

      required.push(
        'notes-corporate-information',
        'notes-accounting-policies',
        'notes-list-of-notes'
      );

      optional.push('notes-issued-capital', 'notes-related-party-transactions');

      return { required, optional };
    }

    // Default: all sheets optional
    return { required, optional: [] };
  }
}
